import { db } from './db';
import { queueRequest } from '../services/syncService';
import { getCachedReferenceData, saveReferenceData } from '../services/prefetchService';
import { getCachedResponse } from '../services/apiCache';
import { getAssetProjectScope, getOfflineUserScope } from './offlineScope';
import { useOfflineStore } from '../stores/offlineStore';
import {
  getPlantAndMachineryScanContext,
  getEquipmentScanContext,
  getAccessEquipmentScanContext,
  getTemporaryWorksScanContext,
  listPlantAndMachineryRecords,
  listEquipmentRecords,
  listAccessEquipmentRecords,
  listTemporaryWorksRecords
} from '../api/assetManagement';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const SCAN_CONTEXT_FETCHERS = {
  'plant-and-machinery': getPlantAndMachineryScanContext,
  equipment: getEquipmentScanContext,
  'access-equipment': getAccessEquipmentScanContext,
  'temporary-works': getTemporaryWorksScanContext
};

const REGISTER_LIST_FETCHERS = {
  'plant-and-machinery': listPlantAndMachineryRecords,
  equipment: listEquipmentRecords,
  'access-equipment': listAccessEquipmentRecords,
  'temporary-works': listTemporaryWorksRecords
};

export const ASSET_VARIANTS = [
  'plant-and-machinery',
  'equipment',
  'access-equipment',
  'temporary-works'
];

const REGISTER_CACHE_PREFIX = 'assetRegister_';
const TYPES_CACHE_PREFIX = 'assetTypes_';
const SCAN_CONTEXT_PREFIX = 'assetScanContext_';

export function registerCacheKey(variant) {
  return `${REGISTER_CACHE_PREFIX}${variant}`;
}

export function typesCacheKey(variant) {
  return `${TYPES_CACHE_PREFIX}${variant}`;
}

export function scanContextCacheKey(variant, recordId) {
  return `${SCAN_CONTEXT_PREFIX}${variant}_${recordId}`;
}

/** Max age for cached scan/checklist context used in offline daily checks. */
export const SCAN_CONTEXT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Skip network prefetch when a scan context was cached more recently than this. */
const PREFETCH_CACHE_FRESH_MS = 12 * 60 * 60 * 1000;

const PREFETCH_CONCURRENCY = 2;
const PREFETCH_BATCH_PAUSE_MS = 75;

/** One in-flight full-project prefetch per project (dashboard + asset home both call this). */
const projectPrefetchInFlight = new Map();

function isRateLimitedError(error) {
  const status = error?.response?.status ?? error?.status;
  return status === 429;
}

function isOnSiteRecord(row) {
  const status = String(row?.siteStatus || row?.status || '')
    .trim()
    .toLowerCase();
  if (!status) return true;
  if (status.includes('off site') || status.includes('archived')) return false;
  if (status.includes('pending return')) return false;
  return status.includes('on site') || status === 'on-site' || status === 'onsite';
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function cacheRegisterRecords(projectId, variant, records) {
  if (!projectId || !variant) return;
  await saveReferenceData(registerCacheKey(variant), getAssetProjectScope(projectId), records || []);
}

export async function getCachedRegisterRecords(projectId, variant, ignoreExpiry = true) {
  return getCachedReferenceData(registerCacheKey(variant), getAssetProjectScope(projectId), ignoreExpiry);
}

export async function cacheAssetTypes(projectId, variant, types) {
  if (!projectId || !variant) return;
  await saveReferenceData(typesCacheKey(variant), getAssetProjectScope(projectId), types || []);
}

export async function getCachedAssetTypes(projectId, variant, ignoreExpiry = true) {
  return getCachedReferenceData(typesCacheKey(variant), getAssetProjectScope(projectId), ignoreExpiry);
}

export async function cacheScanContext(variant, recordId, context) {
  if (!variant || !recordId) return;
  await saveReferenceData(scanContextCacheKey(variant, recordId), getOfflineUserScope(), context);
}

export async function getCachedScanContext(variant, recordId, ignoreExpiry = true) {
  const ctx = await getCachedReferenceData(
    scanContextCacheKey(variant, recordId),
    getOfflineUserScope(),
    ignoreExpiry
  );
  if (ctx) return ctx;
  // Legacy entries cached before per-user scoping
  return getCachedReferenceData(scanContextCacheKey(variant, recordId), 'global', ignoreExpiry);
}

function buildScanContextApiCacheUrl(variant, recordId) {
  return `${API_BASE}/api/asset-management/scan/${variant}/${recordId}`
    .replace(/\/+$/, '')
    .toLowerCase();
}

async function getScanContextFromApiCache(variant, recordId) {
  const cached = await getCachedResponse(buildScanContextApiCacheUrl(variant, recordId), true);
  if (!cached) return null;
  return cached?.data ?? cached;
}

async function readScanContextFromCaches(variant, recordId) {
  const reference = await getCachedScanContext(variant, recordId, true);
  if (reference) return { context: reference, source: 'reference' };

  const apiCached = await getScanContextFromApiCache(variant, recordId);
  if (apiCached) {
    const context = apiCached?.data ?? apiCached;
    if (context && typeof context === 'object' && Object.keys(context).length) {
      await cacheScanContext(variant, recordId, context);
    }
    return { context, source: 'apiCache' };
  }

  return null;
}

/**
 * Load daily-check scan context for operative flows (online or offline).
 */
export async function loadScanContextForDailyCheck(variant, recordId, fetchFn) {
  if (!recordId) {
    throw new Error('Missing asset id for daily check');
  }

  if (isOfflineNow()) {
    const hit = await readScanContextFromCaches(variant, recordId);
    if (hit?.context) return hit;
    const err = new Error(
      'Daily check not available offline yet. While online, open Asset Management for this project once so checklists can download.'
    );
    err.isOffline = true;
    throw err;
  }

  try {
    const data = await fetchFn(recordId);
    await cacheScanContext(variant, recordId, data);
    return { context: data, source: 'network' };
  } catch (error) {
    const hit = await readScanContextFromCaches(variant, recordId);
    if (hit?.context && (isOfflineError(error) || error?.response?._fromCache)) {
      return hit;
    }
    if (hit?.context && isOfflineError(error)) {
      return hit;
    }
    throw error;
  }
}

/**
 * Prefetch one scan context only if missing or stale. Returns:
 *   'cached' | 'fetched' | 'rate-limited' | 'failed'
 */
async function prefetchOneScanContext(variant, recordId, fetchFn) {
  const age = await getScanContextCacheAge(variant, recordId);
  if (age != null && age < PREFETCH_CACHE_FRESH_MS) {
    return 'cached';
  }

  const existing = await readScanContextFromCaches(variant, recordId);
  if (existing?.context && (age == null || age < PREFETCH_CACHE_FRESH_MS)) {
    return 'cached';
  }

  try {
    const data = await fetchFn(recordId);
    await cacheScanContext(variant, recordId, data);
    return 'fetched';
  } catch (err) {
    if (isRateLimitedError(err)) {
      console.warn(`[prefetch] rate limited on ${variant}/${recordId} — stopping`);
      return 'rate-limited';
    }
    console.warn(`[prefetch] scan context ${variant}/${recordId}:`, err?.message || err);
    return 'failed';
  }
}

async function prefetchScanContextsForRecordIds(variant, recordIds, fetchFn, concurrency = PREFETCH_CONCURRENCY) {
  const ids = [...new Set((recordIds || []).filter(Boolean))];
  if (!ids.length || isOfflineNow()) return { fetched: 0, cached: 0, stopped: false };

  let fetched = 0;
  let cached = 0;

  for (let i = 0; i < ids.length; i += concurrency) {
    const batch = ids.slice(i, i + concurrency);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(batch.map((recordId) => prefetchOneScanContext(variant, recordId, fetchFn)));

    if (results.includes('rate-limited')) {
      return { fetched, cached, stopped: true };
    }
    for (const result of results) {
      if (result === 'fetched') fetched += 1;
      if (result === 'cached') cached += 1;
    }

    if (i + concurrency < ids.length && PREFETCH_BATCH_PAUSE_MS > 0) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(PREFETCH_BATCH_PAUSE_MS);
    }
  }

  return { fetched, cached, stopped: false };
}

/**
 * Warm scan context cache before navigating to daily check (online only).
 */
export async function warmScanContextForDailyCheck(variant, recordId) {
  const fetchFn = SCAN_CONTEXT_FETCHERS[variant];
  if (!recordId || !fetchFn || isOfflineNow()) return;
  try {
    await loadScanContextForDailyCheck(variant, recordId, fetchFn);
  } catch (err) {
    console.warn(`[warm] scan context ${variant}/${recordId}:`, err?.message || err);
  }
}

/**
 * After register rows load, prefetch scan contexts for yard offline use.
 * On-site assets only; skips fresh cache; stops on HTTP 429.
 */
export async function prefetchScanContextsForRegisterRecords(projectId, variant, records) {
  const fetchFn = SCAN_CONTEXT_FETCHERS[variant];
  if (!projectId || !fetchFn || isOfflineNow()) return;

  const eligibleIds = (records || [])
    .filter(isOnSiteRecord)
    .map((row) => row.id)
    .filter(Boolean);

  await prefetchScanContextsForRecordIds(variant, eligibleIds, fetchFn);
}

/**
 * Prefetch scan contexts for on-site assets in a project (all variants).
 * Dedupes concurrent callers; skips fresh caches; stops on rate limit.
 */
export async function prefetchAllScanContextsForProject(projectId) {
  if (!projectId || isOfflineNow()) return;

  const existing = projectPrefetchInFlight.get(projectId);
  if (existing) return existing;

  const run = (async () => {
    for (const variant of ASSET_VARIANTS) {
      const listFn = REGISTER_LIST_FETCHERS[variant];
      const fetchFn = SCAN_CONTEXT_FETCHERS[variant];
      if (!listFn || !fetchFn) continue;

      try {
        let records = await getCachedRegisterRecords(projectId, variant, true);
        if (!records?.length) {
          records = await listFn(projectId);
          await cacheRegisterRecords(projectId, variant, records);
        }
        const result = await prefetchScanContextsForRecordIds(
          variant,
          (records || []).filter(isOnSiteRecord).map((row) => row.id).filter(Boolean),
          fetchFn
        );
        if (result.stopped) {
          console.warn(`[prefetch] stopped early for project ${projectId} (${variant})`);
          break;
        }
      } catch (err) {
        if (isRateLimitedError(err)) {
          console.warn(`[prefetch] rate limited listing ${variant} — stopping`);
          break;
        }
        console.warn(`[prefetch] all scan contexts ${variant}:`, err?.message || err);
      }
    }
  })().finally(() => {
    projectPrefetchInFlight.delete(projectId);
  });

  projectPrefetchInFlight.set(projectId, run);
  return run;
}

export async function getScanContextCacheAge(variant, recordId) {
  try {
    const key = scanContextCacheKey(variant, recordId);
    let record = await db.referenceData.get({ key, scope: getOfflineUserScope() });
    if (!record?.updated_at) {
      record = await db.referenceData.get({ key, scope: 'global' });
    }
    if (!record?.updated_at) return null;
    return Date.now() - record.updated_at;
  } catch {
    return null;
  }
}

/**
 * Validate cached checklist context before queuing an offline daily check.
 */
export async function validateScanContextForOfflineSubmit(variant, recordId, context = null) {
  const age = await getScanContextCacheAge(variant, recordId);
  if (age != null && age > SCAN_CONTEXT_MAX_AGE_MS) {
    return {
      ok: false,
      reason:
        'Cached checklist data is over 24 hours old. Go online and reopen this daily check before submitting.'
    };
  }

  let ctx = context;
  if (!ctx) {
    ctx = await getCachedScanContext(variant, recordId, true);
  }

  if (!ctx?.checklistReady) {
    return {
      ok: false,
      reason:
        'No published checklist is available offline. Go online and load this asset before submitting.'
    };
  }

  return { ok: true, checklistVersion: ctx.checklist?.checklistVersion };
}

export function hasDeferredAttachments(attachments = []) {
  return attachments.some((att) => Boolean(att?._offlineFile));
}

export function hasDeferredAttachmentsInFaults(faults = []) {
  return faults.some((fault) => hasDeferredAttachments(fault?.attachments));
}

export function needsOfflineAttachmentSync(inspectionAttachments = [], faults = []) {
  return hasDeferredAttachments(inspectionAttachments) || hasDeferredAttachmentsInFaults(faults);
}

export function isOfflineNow() {
  const storeOnline = useOfflineStore.getState().isOnline;
  return !navigator.onLine || storeOnline === false;
}

export function isOfflineError(error) {
  return Boolean(error?.isOffline || error?.code === 'ERR_NETWORK' || !error?.response);
}

/** Timeout — request may have reached the server; avoid queuing a duplicate offline create. */
export function isAmbiguousSubmitError(error) {
  return error?.code === 'ECONNABORTED';
}

function registerMatchKey(row) {
  const id = row?.fleetNo || row?.itemId || row?.item_id;
  if (!id || id === 'Pending sync' || String(id).startsWith('offline-')) return null;
  return String(id).trim().toLowerCase();
}

export async function fetchOfflineRegisterRecords(projectId, variant) {
  try {
    return await db.assetRegisterRecords
      .where('project_id')
      .equals(projectId)
      .filter((row) => row.variant === variant && (row.synced === 0 || row.synced === false))
      .toArray();
  } catch {
    return [];
  }
}

function offlineRowToRegisterRecord(row) {
  const d = row.display || {};
  return {
    id: row.record_id || `offline-${row.client_id}`,
    fleetNo: d.fleetNo || 'Pending sync',
    assetType: d.assetType || d.typeName || '-',
    regSerial: d.regSerial || '-',
    owner: d.owner || '-',
    siteStatus: d.siteStatus || 'On Site',
    onSiteSince: d.onSiteSince || null,
    collected: d.collected ?? false,
    expiredCerts: d.expiredCerts ?? 0,
    lastInspected: d.lastInspected || null,
    openFaults: d.openFaults ?? 0,
    _offlinePending: true,
    _clientId: row.client_id
  };
}

export function mergeRegisterRecords(serverRecords = [], offlineRows = []) {
  const merged = [...(serverRecords || [])];
  const serverKeys = new Set(merged.map(registerMatchKey).filter(Boolean));

  for (const row of offlineRows) {
    if (row.synced === 1 || row.synced === true) continue;
    const offlineRecord = offlineRowToRegisterRecord(row);
    const key = registerMatchKey(offlineRecord);
    if (key && serverKeys.has(key)) continue;
    merged.unshift(offlineRecord);
  }
  return merged;
}

function extractFleetNoFromPayload(payload) {
  const fv = payload?.fieldValues || {};
  for (const [key, value] of Object.entries(fv)) {
    const lower = key.toLowerCase();
    if (
      lower.includes('fleet') ||
      (lower.includes('item') && lower.includes('id')) ||
      lower === 'itemid'
    ) {
      const v = String(value ?? '').trim();
      if (v) return v;
    }
  }
  const keys = Object.keys(fv);
  for (const key of keys) {
    const lower = key.toLowerCase();
    if (lower.includes('fleet') || (lower.includes('asset') && lower.includes('no'))) {
      return fv[key];
    }
  }
  const first = keys.find((k) => fv[k]);
  return first ? fv[first] : 'Pending sync';
}

export async function submitAssetRecordOffline({
  variant,
  projectId,
  isEdit,
  recordId,
  formData,
  typeName
}) {
  const endpoint = isEdit
    ? `/asset-management/projects/${projectId}/${variant}/${recordId}`
    : `/asset-management/projects/${projectId}/${variant}`;
  const method = isEdit ? 'PUT' : 'POST';

  const files = [];
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      files.push({ fieldName: key, file: value });
    }
  }

  const payloadStr = formData.get('payload');
  const parsedPayload = payloadStr ? JSON.parse(payloadStr) : {};

  const { syncId, clientId } = await queueRequest(
    'assetRecord',
    isEdit ? 'update' : 'create',
    { payload: payloadStr },
    { endpoint, method, files, projectId }
  );

  if (!isEdit) {
    await db.assetRegisterRecords.add({
      project_id: projectId,
      variant,
      client_id: clientId,
      sync_queue_id: syncId,
      record_id: null,
      synced: 0,
      created_at: new Date().toISOString(),
      display: {
        fleetNo: extractFleetNoFromPayload(parsedPayload),
        assetType: typeName || '-',
        typeName: typeName || '-',
        siteStatus: 'On Site'
      }
    });
  }

  return { id: `offline-${clientId}`, _offline: true, clientId };
}

export async function persistAssetRecordFormOffline({
  variant,
  projectId,
  isEdit,
  recordId,
  formData,
  typeName,
  onAdded,
  onUpdated,
  onClose,
  addedMessage = 'Asset saved offline — will sync when back online',
  updatedMessage = 'Changes saved offline — will sync when back online'
}) {
  const record = await submitAssetRecordOffline({
    variant,
    projectId,
    isEdit,
    recordId,
    formData,
    typeName
  });
  if (isEdit) {
    onUpdated?.(record);
    toast.success(updatedMessage);
  } else {
    onAdded?.(record);
    toast.success(addedMessage);
  }
  onClose?.();
  return record;
}

export function buildOfflineAttachmentFiles(attachments = [], prefix, filesOut) {
  const resolved = [];
  for (let i = 0; i < attachments.length; i += 1) {
    const att = attachments[i];
    if (att?._offlineFile) {
      const fieldName = `${prefix}_${i}`;
      filesOut.push({
        fieldName,
        file: att._offlineFile,
        photoNote: att.photo_note || ''
      });
      resolved.push({
        file_name: att.file_name,
        file_type: att.file_type,
        file_size: att.file_size,
        photo_note: att.photo_note || '',
        _offline_ref: fieldName
      });
    } else if (att?.file_path) {
      resolved.push(att);
    }
  }
  return resolved;
}

export async function submitAssetDailyCheckOffline({
  variant,
  recordId,
  projectId,
  responses,
  signatureData,
  faults,
  inspectionAttachments,
  context = null
}) {
  const validation = await validateScanContextForOfflineSubmit(variant, recordId, context);
  if (!validation.ok) {
    throw new Error(validation.reason);
  }

  const endpoint = `/asset-management/scan/${variant}/${recordId}`;
  const files = [];

  const resolvedInspectionAttachments = buildOfflineAttachmentFiles(
    inspectionAttachments,
    'inspection_attachment',
    files
  );

  const resolvedFaults = (faults || []).map((fault, faultIdx) => ({
    ...fault,
    attachments: buildOfflineAttachmentFiles(
      fault.attachments,
      `fault_attachment_${faultIdx}`,
      files
    )
  }));

  const { syncId, clientId } = await queueRequest(
    'assetDailyCheck',
    'create',
    {
      variant,
      recordId,
      responses,
      signatureData,
      faults: resolvedFaults,
      attachments: resolvedInspectionAttachments
    },
    { endpoint, method: 'POST', files, projectId }
  );

  await db.assetDailyChecks.add({
    record_id: recordId,
    variant,
    client_id: clientId,
    sync_queue_id: syncId,
    synced: 0,
    submitted_at: new Date().toISOString()
  });

  return { clientId };
}

export async function submitAssetFaultCloseOffline({
  variant,
  projectId,
  recordId,
  faultId,
  closureNotes,
  closeoutAttachments
}) {
  const endpoint = `/asset-management/projects/${projectId}/${variant}/${recordId}/faults/${faultId}/close`;
  const files = [];
  const resolvedAttachments = buildOfflineAttachmentFiles(
    closeoutAttachments,
    'closeout_attachment',
    files
  );

  await queueRequest(
    'assetFaultClose',
    'update',
    {
      variant,
      recordId,
      faultId,
      closureNotes,
      projectId,
      attachments: resolvedAttachments
    },
    { endpoint, method: 'PATCH', files, projectId }
  );
}

export async function markOfflineRegisterRecordsSynced(clientIds = []) {
  if (!db.assetRegisterRecords || !clientIds.length) return;
  const idSet = new Set(clientIds);
  await db.assetRegisterRecords
    .filter((row) => idSet.has(row.client_id) && (row.synced === 0 || row.synced === false))
    .modify({ synced: 1 });
}

export async function markOfflineDailyChecksSynced(clientIds = []) {
  if (!db.assetDailyChecks || !clientIds.length) return;
  const idSet = new Set(clientIds);
  await db.assetDailyChecks
    .filter((row) => idSet.has(row.client_id) && (row.synced === 0 || row.synced === false))
    .modify({ synced: 1 });
}
